/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/app/composition/components/ErrorBoundary.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/app/composition/components/ErrorBoundary.tsx
 * filename: ErrorBoundary.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { Component, type ReactNode } from 'react';
import { captureComponentError } from '@/platform/observability/error-telemetry';
import { debugReactError } from '@/platform/observability/debug-logger';
import { SurfaceState } from '@/platform/design-system/components/ui/SurfaceState';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches render errors and prevents white screens.
 * Displays a recovery UI instead of a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    captureComponentError(error, errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown');
    debugReactError(errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown', error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <SurfaceState
          kind="unavailable"
          title="This surface could not be displayed"
          description={
            this.state.error?.message ||
            'Hyperion could not verify this view. No empty or zero result has been assumed.'
          }
          recoveryHint="Retry the render first. Reload only if the same failure continues."
          action={{ label: 'Try again', onClick: this.handleReset }}
          secondaryAction={{ label: 'Reload page', onClick: () => window.location.reload() }}
          className="m-6 min-h-[400px]"
        />
      );
    }

    return this.props.children;
  }
}
